import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ModalHistoricoMesa.module.css';
import { FiClock, FiX } from 'react-icons/fi';
import { numeroExibicaoPedido } from '../utils/pedido';

const STATUS_LABEL = {
  pendente: 'Pendente',
  'em andamento': 'Em andamento',
  pronto: 'Pronto',
  entregue: 'Entregue',
  cancelado: 'Cancelado'
};

function formatarDataHora(data) {
  return new Date(data).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

// Histórico de pedidos de uma mesa — uma sessão (conta) por vez, cada uma com
// os pedidos lançados nela. Usado pelo operador a partir do Mapa de Mesas.
function ModalHistoricoMesa({ mesa, onClose }) {
  const [historico, setHistorico] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;
  const numero = String(mesa.numero_da_mesa ?? mesa.id_mesa).padStart(2, '0');

  useEffect(() => {
    const buscarHistorico = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/mesas/${mesa.id_mesa}/historico`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Erro ao buscar histórico da mesa.');
        }
        const data = await response.json();
        setHistorico(data);
      } catch (error) {
        setErro(error.message);
      } finally {
        setCarregando(false);
      }
    };

    buscarHistorico();
  }, [API_URL, mesa.id_mesa]);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.cabecalho}>
          <h2>Histórico · Mesa {numero}</h2>
          <button className={styles.btnFechar} onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <div className={styles.corpo}>
          {carregando && <p className={styles.aviso}>Carregando histórico...</p>}

          {!carregando && erro && <p className={styles.erro}>{erro}</p>}

          {!carregando && !erro && historico?.length === 0 && (
            <p className={styles.aviso}>Nenhum pedido registrado nesta mesa ainda.</p>
          )}

          {!carregando && !erro && historico?.map((conta) => (
            <div key={conta.id_conta_mesa} className={styles.sessao}>
              <div className={styles.sessaoCabecalho}>
                <span className={`${styles.sessaoStatus} ${conta.status === 'aberta' ? styles.sessaoAberta : styles.sessaoFechada}`}>
                  {conta.status === 'aberta' ? 'Conta aberta' : 'Conta fechada'}
                </span>
                <span className={styles.sessaoData}>
                  <FiClock size={12} /> {formatarDataHora(conta.aberta_em)}
                  {conta.fechada_em && ` – ${formatarDataHora(conta.fechada_em)}`}
                </span>
                <span className={styles.sessaoTotal}>R$ {parseFloat(conta.total).toFixed(2)}</span>
              </div>

              {conta.pedidos.length === 0 ? (
                <p className={styles.semPedidos}>Nenhum pedido nesta conta.</p>
              ) : (
                conta.pedidos.map((pedido) => (
                  <div key={pedido.id_pedido} className={styles.pedidoCard}>
                    <div className={styles.pedidoCabecalho}>
                      <span className={styles.pedidoNum}>Pedido #{numeroExibicaoPedido(pedido)}</span>
                      <span className={`${styles.pedidoBadge} ${styles['badge_' + pedido.status.replace(/\s+/g, '')]}`}>
                        {STATUS_LABEL[pedido.status] || pedido.status}
                      </span>
                      <span className={styles.pedidoHora}>{formatarDataHora(pedido.data_pedido)}</span>
                    </div>
                    {pedido.lancado_por && (
                      <p className={styles.pedidoAutor}>Lançado por: {pedido.lancado_por}</p>
                    )}

                    <div className={styles.pedidoItens}>
                      {pedido.itens.map((item) => (
                        <div key={item.id_item} className={styles.itemLinha}>
                          <div className={styles.itemLinhaTopo}>
                            <span className={styles.itemQtd}>{item.quantidade}×</span>
                            <span className={styles.itemNome}>{item.produto?.nome_item || 'Produto'}</span>
                            <span className={styles.itemValor}>R$ {parseFloat(item.total_item).toFixed(2)}</span>
                          </div>
                          {item.observacao && (
                            <p className={styles.itemObservacao}>Obs: {item.observacao}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className={styles.pedidoRodape}>
                      <span>{pedido.forma_de_pagamento}</span>
                      <span>R$ {parseFloat(pedido.total).toFixed(2)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ModalHistoricoMesa;
